import React from 'react'
import PropTypes from 'prop-types'
import { addMonths } from 'date-fns'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { usePagination } from '../../components/Tables/Pagination.hooks'
import { useViewportSize } from '../../modules/hooks/viewport'
import { capitalize } from '../../modules/utils/helpers'
import { formatDate } from '../../modules/utils/formatters'
import { onChangeIdleRpmGroupPage } from './IdleRpm.actions'
import {
  onGetReinforcementThunk,
  onLoadOperatorsThunk,
  onSetInitialState
} from '../Reinforcement/Reinforcement.actions'
import Alert from '../../components/Basics/Alert'
import Pagination from '../../components/Tables/Pagination'
import Skeleton from '../../components/Placeholders/Skeleton'
import Table from '../../components/Tables/Table'
import TableCell from '../../components/Tables/TableCell'
import TableRow from '../../components/Tables/TableRow'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import Tag from '../../components/Basics/Tag'
import Typography from '../../components/Basics/Typography'
import { FooterWrapper, IconButton, Strong, SummaryWrapper, TableContainer, WarningIcon } from './IdleRpm.styles'

/**
 * The Idle RPM Table's container.
 */
const IdleRpmTable = props => {
  const { group, groupId, onToggleModal } = props
  const dispatch = useDispatch()
  const history = useHistory()
  const { isExtraTiny, isTiny } = useViewportSize()
  const {
    consultationDate,
    [groupId]: { data, error, loading, page }
  } = useSelector(state => state.idleRpm)
  const currentDate = addMonths(new Date(), -1)

  const { page: currentPage, totalPages, onChangePage, onGetCurrentPage } = usePagination({
    changeCallback: page => dispatch(onChangeIdleRpmGroupPage({ groupId, page })),
    data,
    initialPage: page,
    rowsPerPage: 5
  })

  const handleShowReinforcement = (operatorId, operatorName) => () => {
    if (isTiny) {
      batch(() => {
        dispatch(onSetInitialState({ groupId, reportPeriod: formatDate(consultationDate, 'yyyy-MM'), type: 'RALENTI' }))
        dispatch(onLoadOperatorsThunk({ currentOperator: { operatorId, operatorName }, groupId, type: 'RALENTI' }))
        history.push('/consumo-operador/ralenti/reforzamiento')
      })
    } else {
      batch(() => {
        dispatch(onSetInitialState({ groupId, reportPeriod: formatDate(consultationDate, 'yyyy-MM'), type: 'RALENTI' }))
        dispatch(onLoadOperatorsThunk({ currentOperator: { operatorId, operatorName }, groupId, type: 'RALENTI' }))
        onToggleModal()
      })
    }
  }

  const handleGetReinforcement = reinforcementId => () => {
    if (isTiny) {
      history.push(`/consumo-operador/ralenti/reforzamiento/${reinforcementId}`)
    } else {
      batch(() => {
        dispatch(onGetReinforcementThunk({ reinforcementId, type: 'RALENTI' }))
        onToggleModal()
      })
    }
  }

  return (
    <>
      {isTiny && (
        <SummaryWrapper margin="-10px 0 10px">
          <SummaryWrapper margin="2px 24px 2px 0">
            <Tag color="primary" margin="0 10px 0 0" text={group.p8IdleRpm} />
            <Typography variant="body2">P80 tiempo en ralentí</Typography>
          </SummaryWrapper>

          <SummaryWrapper margin="2px 0">
            <Tag color="primary" margin="0 10px 0 0" text={group.sumLitersOverConsumtion} />
            <Typography variant="body2">Litros sobreconsumo</Typography>
          </SummaryWrapper>
        </SummaryWrapper>
      )}

      {loading && !error && <TablePlaceholder />}

      {!loading && !error && (
        <TableContainer>
          <Table accessibility={{ label: 'Grupo 1' }}>
            <TableRow head>
              <TableCell minWidth="150px">Operador</TableCell>
              <TableCell minWidth="150px">Sum Turnos Operativos</TableCell>
              <TableCell minWidth="150px">Sum hrs motor encendido</TableCell>
              <TableCell minWidth="150px">Sum Horas Ralentí</TableCell>
              <TableCell minWidth="155px">% Tpo operativo en ralentí</TableCell>
              <TableCell minWidth="155px">Consumo total en ralentí vacio</TableCell>
              <TableCell minWidth="150px">Registrar reforzamiento</TableCell>
            </TableRow>

            {onGetCurrentPage().map((row, index) => {
              return (
                <TableRow key={`operador-${index}`}>
                  <TableCell align="center" minWidth="150px">
                    {row.operatorName}
                  </TableCell>
                  <TableCell align="center" minWidth="150px">
                    {`${row.sumOperativeShifts} ${row.sumOperativeShifts === '1' ? 'turno' : 'turnos'}`}
                  </TableCell>
                  <TableCell align="center" minWidth="150px">
                    {`${row.sumHoursEngineOn} ${row.sumHoursEngineOn === '1' ? 'hora' : 'horas'}`}
                  </TableCell>
                  <TableCell align="center" minWidth="150px">
                    {`${row.sumHoursIdleRpm} ${row.sumHoursIdleRpm === '1' ? 'hora' : 'horas'}`}
                  </TableCell>
                  <TableCell align="center" minWidth="155px">
                    {row.percentageOperativeHoursIdleRpm}
                  </TableCell>
                  <TableCell align="center" minWidth="155px">
                    {`${row.impactLitersConsumedOperator} ${
                      row.impactLitersConsumedOperator === '1' ? 'litro' : 'litros'
                    }`}
                  </TableCell>
                  <TableCell align="center" minWidth="150px">
                    {row.hasReinforcement && (
                      <IconButton onClick={handleGetReinforcement(row.reinforcementId)}>
                        <img src="/assets/icons/vectorReportDone.svg" alt="Reportar Recomendación" />
                      </IconButton>
                    )}
                    {!row.hasReinforcement && (
                      <IconButton onClick={handleShowReinforcement(row.operatorId, row.operatorName)}>
                        <img src="/assets/icons/vectorMustReport.svg" alt="Reportar Recomendación" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </Table>
        </TableContainer>
      )}

      {!error && (
        <FooterWrapper margin={data.length > 5 ? '4px 0 0' : '9px 0 0'}>
          {loading && <Skeleton variant="text" width={isExtraTiny ? '100%' : '300px'} />}
          {!loading && (
            <Typography color="disabled-gray" margin="0 12px" variant="subtitle">
              {consultationDate && formatDate(consultationDate, 'yyyy-MM') !== formatDate(currentDate, 'yyyy-MM') && (
                <WarningIcon />
              )}
              {capitalize(formatDate(consultationDate, 'MMMM'))} - Operadores que sobrepasan el porcentaje óptimo de
              tiempo operativo en ralentí.
            </Typography>
          )}

          {!loading && data.length > 5 && (
            <Pagination alignment="right" count={totalPages} page={currentPage} onChange={onChangePage} />
          )}
        </FooterWrapper>
      )}

      {!loading && error && (
        <Alert severity="default">
          <Strong>Lo sentimos, estamos presentando problemas con nuestro servidor</Strong>
          <br />
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Alert>
      )}
    </>
  )
}

IdleRpmTable.defaultProps = {
  onToggleModal: () => undefined
}
IdleRpmTable.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    p8IdleRpm: PropTypes.string.isRequired,
    sumLitersOverConsumtion: PropTypes.string.isRequired
  }).isRequired,
  groupId: PropTypes.string.isRequired,
  onToggleModal: PropTypes.func
}

export default IdleRpmTable
